import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Download, CalendarDays, Utensils, GlassWater, Flame, Plus, Edit3, Trash2, X, Save, ExternalLink, Receipt } from "lucide-react";
import { toast } from "sonner";
import api, { API, getToken } from "@/lib/api";
import { formatTime12 } from "@/lib/format";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function buildMatrix(year, month) {
  // month: 1-12. Returns 6-row matrix of {day, inMonth, date}
  const first = new Date(year, month - 1, 1);
  const startWeekday = first.getDay(); // 0 = Sun
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  // Leading blanks
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  // Chunks of 7
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

function ServiceIcons({ ev }) {
  if (ev.source === "invoice") {
    return <Receipt size={10} className="text-[#C9963F]" strokeWidth={2} />;
  }
  return (
    <span className="inline-flex gap-1">
      {ev.catering_food && <Utensils size={10} className="text-[#C9963F]" strokeWidth={2} />}
      {ev.catering_mocktail && <GlassWater size={10} className="text-[#C9963F]" strokeWidth={2} />}
      {ev.catering_hookah && <Flame size={10} className="text-[#C9963F]" strokeWidth={2} />}
    </span>
  );
}

export default function Calendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [editing, setEditing] = useState(null); // { mode: 'create'|'edit', date, event? }
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/calendar?year=${year}&month=${month}`);
      setDays(data.days || {});
    } catch {
      toast.error("Could not load calendar");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [year, month]);

  function openCreate(day) {
    const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    setEditing({ mode: "create", date: iso });
  }

  async function openEdit(eventId) {
    try {
      const { data } = await api.get(`/contracts/${eventId}`);
      setEditing({ mode: "edit", date: data.event_date, event: data });
    } catch {
      toast.error("Could not load event");
    }
  }

  async function removeEvent(eventId) {
    if (!window.confirm("Delete this catering event?")) return;
    try {
      await api.delete(`/contracts/${eventId}`);
      toast.success("Event deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  }

  async function saveEvent(form) {
    const payload = {
      client_name: form.client_name,
      client_phone: form.client_phone || "",
      event_date: form.event_date,
      event_time: form.event_time || "",
      event_location: form.event_location || "",
      guest_count: Number(form.guest_count) || 0,
      catering_food: !!form.catering_food,
      catering_mocktail: !!form.catering_mocktail,
      catering_hookah: !!form.catering_hookah,
      menu_description: form.menu_description || "",
      internal_notes: form.internal_notes || "",
    };
    try {
      if (editing.mode === "edit" && editing.event) {
        await api.put(`/contracts/${editing.event.id}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/contracts", payload);
        toast.success("Event added");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  }

  const matrix = useMemo(() => buildMatrix(year, month), [year, month]);

  function go(delta) {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m);
    setYear(y);
    setSelectedDay(null);
  }

  function goToday() {
    const t = new Date();
    setMonth(t.getMonth() + 1);
    setYear(t.getFullYear());
    setSelectedDay(null);
  }

  async function downloadPdf() {
    try {
      const res = await fetch(`${API}/calendar.pdf?year=${year}&month=${month}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sheesh-calendar-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download calendar PDF");
    }
  }

  const totalEvents = Object.values(days).reduce((a, list) => a + list.length, 0);
  const isToday = (d) =>
    d && today.getFullYear() === year && today.getMonth() + 1 === month && today.getDate() === d;

  const selectedEvents = selectedDay ? (days[selectedDay] || []) : [];

  return (
    <div data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Schedule</div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">Catering Calendar</h1>
          <p className="text-white/50 mt-2 text-sm">
            {totalEvents} event{totalEvents !== 1 ? "s" : ""} in {MONTH_LABELS[month - 1]} {year}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => openCreate(today.getDate())} data-testid="header-add-event"
            className="inline-flex items-center gap-2 border border-[#C9963F]/40 text-[#C9963F] hover:bg-[#C9963F]/10 px-3 py-2 text-xs uppercase tracking-wider">
            <Plus size={14} /> Add Catering
          </button>
          <button onClick={goToday} className="border border-white/15 text-white/80 hover:border-[#C9963F] hover:text-[#C9963F] px-3 py-2 text-xs uppercase tracking-wider" data-testid="today-btn">
            Today
          </button>
          <button onClick={downloadPdf} className="inline-flex items-center gap-2 bg-[#C9963F] text-black font-medium px-4 py-2 text-sm hover:bg-[#A67A2D]" data-testid="download-calendar-pdf">
            <Download size={14} /> Download PDF
          </button>
        </div>
      </div>

      {/* Month navigator */}
      <div className="flex items-center justify-between bg-[#0e0e0e] border border-white/10 px-5 py-3 mb-4">
        <button onClick={() => go(-1)} className="p-2 text-white/70 hover:text-[#C9963F]" data-testid="prev-month" aria-label="Previous month">
          <ChevronLeft size={18} />
        </button>
        <div className="flex items-center gap-3">
          <CalendarDays size={16} className="text-[#C9963F]" strokeWidth={1.5} />
          <h2 className="font-serif-brand text-2xl sm:text-3xl tracking-tight" data-testid="calendar-month-label">
            {MONTH_LABELS[month - 1]} {year}
          </h2>
        </div>
        <button onClick={() => go(1)} className="p-2 text-white/70 hover:text-[#C9963F]" data-testid="next-month" aria-label="Next month">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Grid (desktop / tablet) */}
      <div className="hidden sm:block bg-[#0e0e0e] border border-white/10" data-testid="calendar-grid">
        <div className="grid grid-cols-7 border-b border-white/10">
          {DAY_LABELS.map((d) => (
            <div key={d} className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#C9963F] text-center">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {matrix.flat().map((d, idx) => {
            const evs = d ? (days[d] || []) : [];
            const todayRing = isToday(d);
            return (
              <button
                key={idx}
                disabled={!d}
                onClick={() => {
                  if (!d) return;
                  const evs = days[d] || [];
                  if (evs.length === 0) openCreate(d);
                  else setSelectedDay(d);
                }}
                data-testid={d ? `day-${d}` : undefined}
                className={`group relative min-h-[110px] text-left p-2 border-b border-r border-white/5 align-top transition-colors ${
                  !d ? "bg-white/[0.01]" : "hover:bg-white/[0.03]"
                } ${selectedDay === d ? "bg-[#C9963F]/5" : ""}`}
              >
                {d && (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${todayRing ? "bg-[#C9963F] text-black px-1.5 py-0.5" : "text-white/80"}`}>
                        {d}
                      </span>
                      {evs.length > 0 ? (
                        <span className="text-[9px] text-[#C9963F]">{evs.length}</span>
                      ) : (
                        <Plus size={12} className="text-white/30 opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </div>
                    <div className="space-y-1">
                      {evs.slice(0, 3).map((ev) => (
                        <div key={ev.id} className="text-[10px] text-white/80 bg-[#C9963F]/10 border-l-2 border-[#C9963F] px-1.5 py-0.5 leading-tight">
                          <div className="flex items-center gap-1 truncate">
                            <ServiceIcons ev={ev} />
                            <span className="text-white font-medium truncate">{ev.client_name}</span>
                            {ev.guest_count ? <span className="text-white/50 shrink-0">· {ev.guest_count}g</span> : null}
                          </div>
                          <div className="text-[9px] text-white/50 truncate mt-0.5">
                            {ev.event_time && formatTime12(ev.event_time)}
                            {ev.event_time && ev.event_location ? " · " : ""}
                            {ev.event_location}
                            {!ev.event_time && !ev.event_location ? (ev.source === "invoice" ? (ev.invoice_number || "Invoice") : "Details TBD") : ""}
                          </div>
                        </div>
                      ))}
                      {evs.length > 3 && (
                        <div className="text-[10px] text-white/40">+{evs.length - 3} more</div>
                      )}
                    </div>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile: agenda list */}
      <div className="sm:hidden bg-[#0e0e0e] border border-white/10 divide-y divide-white/5" data-testid="calendar-mobile-list">
        {Object.keys(days).length === 0 && !loading && (
          <div className="p-8 text-center text-white/40 text-sm">No events this month</div>
        )}
        {Object.keys(days).sort((a, b) => a - b).map((d) => (
          <div key={d} className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-[#C9963F] uppercase tracking-widest">
                {new Date(year, month - 1, d).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
              </div>
              <button onClick={() => openCreate(parseInt(d, 10))} data-testid={`mobile-add-${d}`}
                className="text-[10px] uppercase tracking-wider text-[#C9963F] border border-[#C9963F]/40 px-2 py-0.5 inline-flex items-center gap-1">
                <Plus size={10} /> Add
              </button>
            </div>
            {(days[d] || []).map((ev) => (
              <div key={ev.id} className="bg-[#0a0a0a] border-l-2 border-[#C9963F] px-3 py-2 mb-2" data-testid={`mobile-event-${ev.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm">{ev.client_name}{ev.guest_count ? <span className="text-white/50 text-xs"> · {ev.guest_count} guests</span> : null}</span>
                  <ServiceIcons ev={ev} />
                </div>
                <div className="text-xs text-white/50 mt-0.5">
                  {formatTime12(ev.event_time) || "Time TBD"}
                  {ev.event_location ? ` · ${ev.event_location}` : ""}
                </div>
                <div className="flex gap-2 mt-2">
                  {ev.source === "invoice" ? (
                    <button onClick={() => navigate(`/admin/invoices/${ev.id}`)} className="text-[10px] uppercase tracking-wider text-[#C9963F] border border-[#C9963F]/40 px-2 py-0.5 inline-flex items-center gap-1" data-testid={`open-invoice-${ev.id}`}><Receipt size={10} /> {ev.invoice_number || "Invoice"}</button>
                  ) : (
                    <>
                      <button onClick={() => openEdit(ev.id)} className="text-[10px] uppercase tracking-wider text-white/60 border border-white/15 px-2 py-0.5 inline-flex items-center gap-1"><Edit3 size={10} /> Edit</button>
                      <button onClick={() => navigate(`/admin/contracts/${ev.id}`)} className="text-[10px] uppercase tracking-wider text-white/60 border border-white/15 px-2 py-0.5 inline-flex items-center gap-1"><ExternalLink size={10} /> Open</button>
                      <button onClick={() => removeEvent(ev.id)} className="text-[10px] uppercase tracking-wider text-red-300 border border-red-500/30 px-2 py-0.5 inline-flex items-center gap-1"><Trash2 size={10} /> Delete</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
        {/* Mobile: add for arbitrary day */}
        <div className="p-4">
          <button onClick={() => openCreate(today.getDate())} className="w-full inline-flex items-center justify-center gap-2 border border-[#C9963F]/40 text-[#C9963F] py-3" data-testid="mobile-add-any">
            <Plus size={14} /> Add Catering
          </button>
        </div>
      </div>

      {/* Day details panel (desktop) */}
      {selectedDay && (
        <div className="mt-6 bg-[#0e0e0e] border border-[#C9963F]/40 p-6" data-testid="day-details">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif-brand text-2xl">
              {new Date(year, month - 1, selectedDay).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </h3>
            <div className="flex items-center gap-2">
              <button onClick={() => openCreate(selectedDay)} data-testid="day-add-event"
                className="inline-flex items-center gap-1.5 bg-[#C9963F] text-black px-3 py-1.5 text-xs font-medium hover:bg-[#A67A2D]">
                <Plus size={12} /> Add event
              </button>
              <button onClick={() => setSelectedDay(null)} className="text-white/50 hover:text-white text-sm" data-testid="close-day-details">
                Close
              </button>
            </div>
          </div>
          {selectedEvents.length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center">No events on this day yet.</div>
          ) : (
            <div className="space-y-3">
              {selectedEvents.map((ev) => (
                <div key={ev.id} className="bg-[#0a0a0a] border border-white/10 p-4" data-testid={`day-event-${ev.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {ev.source === "invoice" && (
                          <span className="text-[10px] uppercase tracking-wider text-[#C9963F] border border-[#C9963F]/40 px-2 py-0.5 inline-flex items-center gap-1"><Receipt size={10} /> {ev.invoice_number || "Invoice"}</span>
                        )}
                        <div className="text-white font-medium">{ev.client_name}</div>
                      </div>
                      <div className="text-xs text-white/50 mt-0.5">
                        {ev.source === "invoice" ? (ev.status || "Sent") : (formatTime12(ev.event_time) || "Time TBD")}
                        {ev.guest_count ? ` · ${ev.guest_count} guests` : ""}
                        {ev.event_location ? ` · ${ev.event_location}` : ""}
                      </div>
                      {ev.source !== "invoice" && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {ev.catering_food && <span className="text-[10px] uppercase tracking-wider text-[#C9963F] border border-[#C9963F]/30 px-2 py-0.5">Food</span>}
                          {ev.catering_mocktail && <span className="text-[10px] uppercase tracking-wider text-[#C9963F] border border-[#C9963F]/30 px-2 py-0.5">Mocktail</span>}
                          {ev.catering_hookah && <span className="text-[10px] uppercase tracking-wider text-[#C9963F] border border-[#C9963F]/30 px-2 py-0.5">Hookah</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {ev.source === "invoice" ? (
                        <button onClick={() => navigate(`/admin/invoices/${ev.id}`)} data-testid={`open-day-invoice-${ev.id}`}
                          className="p-2 text-white/60 hover:text-[#C9963F]" title="Open invoice"><ExternalLink size={14} /></button>
                      ) : (
                        <>
                          <button onClick={() => openEdit(ev.id)} data-testid={`edit-event-${ev.id}`}
                            className="p-2 text-white/60 hover:text-[#C9963F]" title="Edit"><Edit3 size={14} /></button>
                          <button onClick={() => navigate(`/admin/contracts/${ev.id}`)}
                            className="p-2 text-white/60 hover:text-[#C9963F]" title="Open in full editor"><ExternalLink size={14} /></button>
                          <button onClick={() => removeEvent(ev.id)} data-testid={`delete-event-${ev.id}`}
                            className="p-2 text-white/60 hover:text-red-400" title="Delete"><Trash2 size={14} /></button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editing && (
        <EventDialog
          initial={editing.event || { event_date: editing.date }}
          mode={editing.mode}
          onCancel={() => setEditing(null)}
          onSave={saveEvent}
        />
      )}

      {loading && <div className="mt-4 text-white/40 text-xs">Loading…</div>}
    </div>
  );
}


function EventDialog({ initial, mode, onCancel, onSave }) {
  const [form, setForm] = useState({
    client_name: initial?.client_name || "",
    client_phone: initial?.client_phone || "",
    event_date: initial?.event_date || "",
    event_time: initial?.event_time || "",
    event_location: initial?.event_location || "",
    guest_count: initial?.guest_count || 0,
    catering_food: !!initial?.catering_food,
    catering_mocktail: !!initial?.catering_mocktail,
    catering_hookah: !!initial?.catering_hookah,
    menu_description: initial?.menu_description || "",
    internal_notes: initial?.internal_notes || "",
  });
  const [saving, setSaving] = useState(false);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit() {
    if (!form.client_name.trim()) return toast.error("Client name is required");
    if (!form.event_date) return toast.error("Event date is required");
    setSaving(true);
    try { await onSave(form); }
    finally { setSaving(false); }
  }

  const inputCls = "w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm focus:border-[#C9963F] focus:outline-none text-white placeholder-white/25";
  const lineCls = "w-full bg-transparent border-b border-white/15 px-0 py-2 focus:border-[#C9963F] focus:outline-none text-white";

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onCancel}>
      <div className="bg-[#0e0e0e] border border-[#C9963F]/40 max-w-xl w-full p-6 sm:p-8 relative my-8" onClick={(e) => e.stopPropagation()} data-testid="event-dialog">
        <button onClick={onCancel} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={18} /></button>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">
          {mode === "edit" ? "Edit Catering" : "New Catering"}
        </div>
        <h3 className="font-serif-brand text-2xl mb-6">Quick event details</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <div className="sm:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Client Name *</label>
            <input className={lineCls} value={form.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="dlg-client-name" autoFocus />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Phone</label>
            <input className={lineCls} value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Guest Count</label>
            <input type="number" min="0" className={inputCls} value={form.guest_count} onChange={(e) => set("guest_count", e.target.value)} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Date *</label>
            <input type="date" className={inputCls} value={form.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="dlg-event-date" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Time</label>
            <input type="time" className={inputCls} value={form.event_time} onChange={(e) => set("event_time", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Location / Address</label>
            <input className={lineCls} value={form.event_location} onChange={(e) => set("event_location", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Services</label>
            <div className="flex flex-wrap gap-2">
              {[["catering_food", "Food"], ["catering_mocktail", "Mocktail"], ["catering_hookah", "Hookah"]].map(([k, label]) => (
                <label key={k} className={`cursor-pointer px-3 py-1.5 border text-xs select-none ${form[k] ? "border-[#C9963F] text-[#C9963F] bg-[#C9963F]/10" : "border-white/15 text-white/70 hover:border-white/40"}`}>
                  <input type="checkbox" className="hidden" checked={form[k]} onChange={(e) => set(k, e.target.checked)} />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Menu / Notes (optional)</label>
            <textarea rows={3} className={inputCls} value={form.menu_description} onChange={(e) => set("menu_description", e.target.value)} />
          </div>
        </div>

        <p className="text-[10px] uppercase tracking-wider text-white/40 mt-5">
          {mode === "edit"
            ? "Saves changes directly to this contract."
            : "Saved as a Draft contract. Open it anytime to add pricing, terms, and send to the client."}
        </p>

        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onCancel} className="text-white/60 hover:text-white px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="dlg-save-btn"
            className="bg-[#C9963F] text-black font-medium px-5 py-2 hover:bg-[#A67A2D] text-sm inline-flex items-center gap-2 disabled:opacity-50">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
